import { z } from 'zod';

import type { useAmelioTranslations } from '@/hooks';
import type { TranslationKey } from '@/i18n-key';

import {
    CONTEXT_HTML_MAX_LENGTH,
    CONTEXT_MAX_LENGTH,
    PREPARATION_NOTES_HTML_MAX_LENGTH,
    PREPARATION_NOTES_MAX_LENGTH,
} from '@/pages/ThematicMeetings/constants';
import { htmlToPlainText } from '@/utils/htmlSanitizer';
import { validationMessageKey } from '@/utils/validationMessage';

interface RichTextBounds {
    /** The longest text the editor's counter lets the writer type, and what a refusal cites. */
    maxTextLength: number;
    /**
     * The longest HTML the API stores. Formatting reaches it long before the text bound is in
     * sight: 250 short bold bullet points are 5 000 characters of text and 11 509 of HTML, which
     * the server refuses while the counter still reads 5000 / 5000.
     */
    maxHtmlLength: number;
    tooLongMessage: TranslationKey;
    tooMuchFormattingMessage: TranslationKey;
}

/**
 * One of a meeting's rich-text fields, as the HTML its editor submits. Both bounds are checked,
 * because they count different things and the server enforces the one on the stored HTML: a field
 * that only held the text to its bound let the writer compose notes it then refused, with nothing
 * saying what to cut.
 */
const richTextSchema = (bounds: RichTextBounds) =>
    z
        .string()
        .refine((html) => htmlToPlainText(html).length <= bounds.maxTextLength, {
            message: bounds.tooLongMessage,
        })
        .refine((html) => html.length <= bounds.maxHtmlLength, {
            message: bounds.tooMuchFormattingMessage,
        });

const CONTEXT_BOUNDS: RichTextBounds = {
    maxTextLength: CONTEXT_MAX_LENGTH,
    maxHtmlLength: CONTEXT_HTML_MAX_LENGTH,
    tooLongMessage: 'thematicMeetings.validation.contextMaxLength',
    tooMuchFormattingMessage: 'thematicMeetings.validation.contextTooMuchFormatting',
};

const PREPARATION_NOTES_BOUNDS: RichTextBounds = {
    maxTextLength: PREPARATION_NOTES_MAX_LENGTH,
    maxHtmlLength: PREPARATION_NOTES_HTML_MAX_LENGTH,
    tooLongMessage: 'thematicMeetings.validation.notesMaxLength',
    tooMuchFormattingMessage: 'thematicMeetings.validation.notesTooMuchFormatting',
};

export const meetingContextSchema = richTextSchema(CONTEXT_BOUNDS);
export const meetingPreparationNotesSchema = richTextSchema(PREPARATION_NOTES_BOUNDS);

/** Why the field cannot be saved, translated, or `undefined` when it can. */
const refusalOf =
    (schema: z.ZodType<string>, maxTextLength: number) =>
    (html: string, t: ReturnType<typeof useAmelioTranslations>['t']): string | undefined => {
        const result = schema.safeParse(html);

        return result.success
            ? undefined
            : t(validationMessageKey(result.error.issues[0].message), { max: maxTextLength });
    };

export const validateMeetingContext = refusalOf(meetingContextSchema, CONTEXT_MAX_LENGTH);
export const validateMeetingPreparationNotes = refusalOf(
    meetingPreparationNotesSchema,
    PREPARATION_NOTES_MAX_LENGTH,
);
